import React, { useState } from 'react';
import { SolutionFilterProps } from '../../types/components';

const SolutionFilter: React.FC<SolutionFilterProps> = ({
  filters,
  activeFilters,
  onFilterChange,
  onClearFilters,
}) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Group filters by category
  const filtersByCategory = filters.reduce(
    (acc, filter) => {
      if (!acc[filter.category]) {
        acc[filter.category] = [];
      }
      acc[filter.category].push(filter);
      return acc;
    },
    {} as Record<string, typeof filters>
  );

  const categoryLabels = {
    industry: 'Industry',
    awsService: 'AWS Services',
    solutionType: 'Solution Type',
  };

  // Count active filters per category
  const getActiveCountForCategory = (category: string) => {
    return activeFilters.filter((filterId) => {
      const filter = filters.find((f) => f.id === filterId);
      return filter?.category === category;
    }).length;
  };

  const hasActiveFilters = activeFilters.length > 0;

  return (
    <div className="flex items-center gap-3">
      {/* Filter dropdowns */}
      {Object.entries(filtersByCategory).map(([category, categoryFilters]) => {
        const activeCount = getActiveCountForCategory(category);
        const isOpen = openDropdown === category;

        return (
          <div
            key={category}
            className="relative"
            onMouseEnter={() => setOpenDropdown(category)}
            onMouseLeave={() => setOpenDropdown(null)}
          >
            <button
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
              aria-expanded={isOpen}
              aria-haspopup="true"
            >
              {categoryLabels[category as keyof typeof categoryLabels]}
              {activeCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-blue-600 rounded-full">
                  {activeCount}
                </span>
              )}
              <svg
                className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Dropdown menu */}
            {isOpen && (
              <div className="absolute top-full left-0 pt-1 w-64 z-50">
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                  <div className="p-3">
                    <div className="space-y-2">
                      {categoryFilters.map((filter, index) => {
                        // Check if this is a divider (for industry grouping)
                        if (filter.id.startsWith('industry-divider-')) {
                          return (
                            <div key={filter.id} className="py-2">
                              <div className="border-t border-gray-200"></div>
                            </div>
                          );
                        }

                        const isActive = activeFilters.includes(filter.id);
                        return (
                          <label
                            key={filter.id}
                            className="flex items-center cursor-pointer group hover:bg-gray-50 p-2 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() => onFilterChange(filter.id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 flex-1">
                              {filter.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Clear all button */}
      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Clear All
        </button>
      )}
    </div>
  );
};

export default SolutionFilter;
